import { useState } from 'react';
import { format, differenceInDays, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isToday } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Calendar, Clock, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getRoleColor, getInitials } from '@/lib/roleColors';

interface Task {
  id: string;
  task_title: string;
  status: string;
  due_date: string | null;
  assignees: any[];
  checkpoints: any[];
  category_type: string;
  estimated_hours: number | null;
}

interface TaskTimelineViewProps {
  tasks: Task[];
  onViewDetails: (task: Task) => void;
}

export function TaskTimelineView({ tasks, onViewDetails }: TaskTimelineViewProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get unique assignees across all tasks
  const allAssignees = Array.from(
    new Map(
      tasks.flatMap(t => t.assignees || []).map((a: any) => [a.id || a.user_id, a])
    ).values()
  );

  // Filter tasks by assignee
  const filteredTasks = filterAssignee === 'all' 
    ? tasks 
    : filterAssignee === 'unassigned'
    ? tasks.filter(t => !t.assignees || t.assignees.length === 0)
    : tasks.filter(t => t.assignees?.some((a: any) => (a.id || a.user_id) === filterAssignee));

  // Filter tasks with due dates
  const scheduledTasks = filteredTasks.filter(t => t.due_date);
  const unscheduledTasks = filteredTasks.filter(t => !t.due_date);

  const getTaskDayIndex = (task: Task) => {
    if (!task.due_date) return null;
    
    const dueDate = new Date(task.due_date);
    
    // Skip if due date is outside current month
    if (dueDate < monthStart || dueDate > monthEnd) return null;
    
    return differenceInDays(dueDate, monthStart);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return { bg: 'bg-emerald-500', text: 'text-white', label: 'Completed' };
      case 'in_progress':
        return { bg: 'bg-blue-500', text: 'text-white', label: 'In Progress' };
      case 'blocked':
        return { bg: 'bg-red-500', text: 'text-white', label: 'Blocked' };
      default:
        return { bg: 'bg-amber-500', text: 'text-white', label: 'Not Started' };
    }
  };

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const handleToday = () => setCurrentMonth(new Date());

  const getAssigneeName = (task: Task) => {
    if (!task.assignees || task.assignees.length === 0) return 'Unassigned';
    const first = task.assignees[0];
    return first.full_name || first.name || 'Assigned';
  };

  return (
    <TooltipProvider>
      <div className="bg-card rounded-lg border shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-wrap gap-4">
          <h2 className="text-xl font-semibold text-foreground">
            Timeline - {format(currentMonth, 'MMMM yyyy')}
          </h2>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Assigned To:</span>
              <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {allAssignees.map((assignee: any) => (
                    <SelectItem key={assignee.id || assignee.user_id} value={assignee.id || assignee.user_id}>
                      {assignee.full_name || assignee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={handlePrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-9" onClick={handleToday}>
                Today
              </Button>
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Timeline Content */}
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Days Header */}
            <div className="flex border-b bg-muted/30">
              <div className="w-64 flex-shrink-0 p-3 font-medium text-sm text-muted-foreground border-r">
                Task
              </div>
              <div className="flex-1 flex">
                {daysInMonth.map((date, i) => {
                  const isTodayDate = isToday(date);
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  return (
                    <div
                      key={i}
                      className={`
                        flex-1 text-center py-2 text-xs font-medium border-r border-border/50 last:border-r-0
                        ${isTodayDate ? 'bg-primary/10 text-primary' : isWeekend ? 'bg-muted/50 text-muted-foreground' : 'text-muted-foreground'}
                      `}
                    >
                      <div>{format(date, 'd')}</div>
                      {isTodayDate && <div className="text-[10px] font-normal">Today</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Task Rows */}
            <div className="divide-y">
              {scheduledTasks.map((task) => {
                const dayIndex = getTaskDayIndex(task);
                if (dayIndex === null) return null;

                const statusConfig = getStatusConfig(task.status);
                const primaryAssignee = task.assignees?.[0];
                const roleColors = primaryAssignee ? getRoleColor(primaryAssignee.member_role || primaryAssignee.role) : null;

                return (
                  <div 
                    key={task.id} 
                    className="flex hover:bg-accent/30 transition-colors cursor-pointer group"
                    onClick={() => onViewDetails(task)}
                  >
                    {/* Task Info Column */}
                    <div className="w-64 flex-shrink-0 p-3 border-r bg-background">
                      <div className="space-y-1.5">
                        <div className="font-medium text-sm text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                          {task.task_title}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Assignee */}
                          <div className="flex items-center gap-1.5">
                            {primaryAssignee ? (
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold"
                                style={{
                                  backgroundColor: roleColors?.bg || 'hsl(var(--muted))',
                                  color: roleColors?.text || 'hsl(var(--muted-foreground))',
                                  border: `1.5px solid ${roleColors?.border || 'hsl(var(--border))'}`
                                }}
                              >
                                {getInitials(primaryAssignee.full_name || primaryAssignee.name || '')}
                              </div>
                            ) : (
                              <User className="w-4 h-4 text-muted-foreground" />
                            )}
                            <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                              {getAssigneeName(task)}
                            </span>
                          </div>
                          {/* Status Badge */}
                          <Badge 
                            variant="secondary" 
                            className={`text-[10px] px-1.5 py-0 h-4 ${statusConfig.bg} ${statusConfig.text} border-0`}
                          >
                            {statusConfig.label}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Timeline Grid */}
                    <div className="flex-1 flex relative">
                      {daysInMonth.map((date, i) => {
                        const isTodayDate = isToday(date);
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                        const isDueDay = i === dayIndex;
                        
                        return (
                          <div
                            key={i}
                            className={`
                              flex-1 border-r border-border/30 last:border-r-0 min-h-[60px] relative
                              ${isTodayDate ? 'bg-primary/5' : isWeekend ? 'bg-muted/30' : ''}
                            `}
                          >
                            {isDueDay && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="absolute inset-1 flex items-center justify-center">
                                    <div 
                                      className={`
                                        w-full h-8 rounded-md flex items-center justify-center gap-1 px-2
                                        ${statusConfig.bg} ${statusConfig.text} shadow-sm
                                        ${task.status === 'completed' ? 'opacity-60' : ''}
                                      `}
                                    >
                                      <Calendar className="w-3 h-3 flex-shrink-0" />
                                      <span className="text-[10px] font-medium truncate">Due</span>
                                    </div>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[250px]">
                                  <div className="space-y-1">
                                    <p className="font-medium">{task.task_title}</p>
                                    <p className="text-xs text-muted-foreground">
                                      Due: {format(new Date(task.due_date!), 'MMM d, yyyy')}
                                    </p>
                                    {task.estimated_hours && (
                                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {task.estimated_hours}h estimated
                                      </p>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {scheduledTasks.filter(t => getTaskDayIndex(t) !== null).length === 0 && (
                <div className="text-center text-muted-foreground py-12">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No tasks scheduled in {format(currentMonth, 'MMMM yyyy')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Unscheduled Tasks */}
        {unscheduledTasks.length > 0 && (
          <div className="border-t p-4">
            <h3 className="font-medium text-sm mb-3 flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              Unscheduled Tasks
              <Badge variant="secondary" className="text-xs">{unscheduledTasks.length}</Badge>
            </h3>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {unscheduledTasks.map(task => {
                const statusConfig = getStatusConfig(task.status);
                return (
                  <div
                    key={task.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => onViewDetails(task)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{task.task_title}</p>
                      <p className="text-xs text-muted-foreground">{getAssigneeName(task)}</p>
                    </div>
                    <Badge 
                      variant="secondary" 
                      className={`text-[10px] px-1.5 py-0 h-4 flex-shrink-0 ${statusConfig.bg} ${statusConfig.text} border-0`}
                    >
                      {statusConfig.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
